import React from 'react'
import { theme } from '../theme'

/**
 * ONE reusable progressive-disclosure control: a small icon that reveals a block of detail.
 *
 * WHY THIS FILE EXISTS
 *
 * The job editor accumulated a dozen long explanatory paragraphs, each one written because
 * somebody had been misled by its absence. Every one of them is still true and still worth
 * having — the problem is that all of them are on screen at once, so the CONTROLS an operator
 * came to change are buried in prose they have already read. The answer is not to delete the
 * text (this application's signature failure is a surface that misdescribes a working engine)
 * but to make reading it a choice.
 *
 * It is a COMPONENT and not per-blob markup on purpose. FINISH-LINE Wave A items 9 through 18
 * ask for the same treatment on the tactical page, the batch detail page and three settings
 * panels; twelve hand-rolled toggles would be twelve chances to get `aria-expanded` wrong, and
 * the hover-only `title=` affordance this project already had to consolidate once (see
 * `Hint.tsx`) is exactly what a per-copy affordance decays into.
 *
 * ACCESSIBILITY — the WAI-ARIA disclosure pattern, deliberately unembellished:
 *
 *   * The trigger is a NATIVE `<button>`. That gives keyboard focus, Enter and Space
 *     activation, and a focus ring from the platform rather than from us. A `<span
 *     role="button" tabIndex={0}>` would need all three re-implemented, and the two projects
 *     that do that here get Space wrong.
 *   * `type="button"` is LOAD-BEARING, not boilerplate. Every consumer added in this change
 *     sits inside the job editor's `<form>`, where a button with the default `type="submit"`
 *     SAVES THE JOB when the operator expands a caveat. A test asserts it.
 *   * `aria-expanded` tracks the state and `aria-controls` names the region, so a screen
 *     reader announces "collapsed"/"expanded" rather than reading a bare icon.
 *   * An accessible name is REQUIRED — `label` is a required prop, so a trigger cannot ship
 *     announcing only "ⓘ button". The glyph itself is `aria-hidden`.
 *   * The collapsed region uses the `hidden` attribute, which removes it from the
 *     accessibility tree as well as from the page. That is the correct choice HERE and the
 *     opposite of `Hint.tsx`'s clipped node: `Hint` keeps its text present because an
 *     `aria-describedby` target must be findable, whereas a disclosure's whole contract is
 *     that closed means closed for everyone equally. A screen-reader user who is told a
 *     region is collapsed and then hears its contents anyway has been lied to.
 *   * Escape collapses an expanded region, matching the affordance next door.
 *
 * COLLAPSED IS THE DEFAULT, AND THE STATE IS NOT PERSISTED. Nothing writes to localStorage,
 * so a reload returns every region to collapsed. That is a decision rather than an omission:
 * a sticky expansion means two operators looking at the same job see different pages, and a
 * screenshot in a ticket stops being reproducible. The state is also intentionally per-mount,
 * so opening a second job starts clean.
 *
 * WHAT MUST NEVER GO INSIDE ONE. A validation error, a save refusal, an API fault or a
 * "cannot be loaded" notice stays on the page unconditionally. An error the operator has to
 * find and expand before they can read it is worse than the long caveat this component
 * exists to hide — it is a silent failure with a disclosure triangle in front of it, and this
 * repository has shipped that defect in quieter forms more than once.
 * `tests/test_job_editor_progressive_disclosure.py` enforces it against the source.
 */

let sequence = 0

/** A DOM id for the region, stable for the lifetime of the component. */
function useRegionId(): string {
  const ref = React.useRef<string | null>(null)
  if (ref.current === null) {
    sequence += 1
    ref.current = `disclosure-${sequence}`
  }
  return ref.current
}

export type DisclosureTone = 'muted' | 'caution'

export interface DisclosureProps {
  /**
   * The trigger's accessible name, phrased as what expanding it will tell you — e.g.
   * "Why the overall window is derived". REQUIRED: a disclosure whose trigger announces only
   * its icon is unusable from a screen reader, so there is deliberately no default.
   */
  label: string
  /**
   * Always-visible lead text, rendered beside the trigger. Use it for the part an operator
   * must see without acting — a derived VALUE, or the one-line statement of a fact whose
   * explanation is the collapsible part.
   */
  summary?: React.ReactNode
  /** The detail. Hidden until the operator asks for it. */
  children: React.ReactNode
  tone?: DisclosureTone
  /** Layout only. The component sets no margin of its own. */
  style?: React.CSSProperties
}

const TONE: Record<DisclosureTone, { text: string; trigger: string }> = {
  muted: { text: theme.textMuted, trigger: theme.textMuted },
  // Amber, matching the existing caveat colour on these pages, so a caution that used to be
  // an amber paragraph still reads as a caution once collapsed.
  caution: { text: '#ffcc80', trigger: '#ffa726' },
}

export function Disclosure({
  label, summary, children, tone = 'muted', style,
}: DisclosureProps) {
  const [open, setOpen] = React.useState(false)
  const id = useRegionId()
  const colours = TONE[tone]

  return (
    <div style={{ fontSize: '0.75rem', color: colours.text, ...style }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
        {summary != null && <span style={{ flex: '1 1 auto' }}>{summary}</span>}
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          onKeyDown={e => { if (e.key === 'Escape') setOpen(false) }}
          aria-expanded={open}
          aria-controls={id}
          aria-label={label}
          title={label}
          style={{
            flex: '0 0 auto',
            background: 'transparent',
            border: `1px solid ${theme.border}`,
            borderRadius: '3px',
            color: colours.trigger,
            cursor: 'pointer',
            font: 'inherit',
            lineHeight: 1,
            padding: '0.1rem 0.3rem',
            outlineOffset: '2px',
          }}
        >
          {/* The glyph carries no information a screen reader needs: `aria-label` above is
              the accessible name, and `aria-expanded` is the state. */}
          <span aria-hidden="true">{open ? '⌃' : 'ⓘ'}</span>
        </button>
      </div>
      <div id={id} hidden={!open} style={{ marginTop: '0.35rem', lineHeight: 1.45 }}>
        {children}
      </div>
    </div>
  )
}

export default Disclosure
